import React, { useEffect, useMemo, useState } from "react";
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Collapse from '@mui/material/Collapse';
import IconButton from '@mui/material/IconButton';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Pagination from '@mui/material/Pagination';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import moment from "moment";
import CustomTooltipComponent from "./custom_tooltip_component";
import CheckOutlinedIcon from '@mui/icons-material/CheckOutlined';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import HourglassTopIcon from '@mui/icons-material/HourglassTop';
import { KeyboardArrowDown, KeyboardArrowUp } from '@mui/icons-material';
import ModeEditOutlinedIcon from '@mui/icons-material/ModeEditOutlined';
import { TrashIcon } from '@heroicons/react/24/outline';
import FormEditRimborsoCompany from "./form_edit_rimborso_company";
import FormEditRimborsoKmCompany from "./form_edit_rimborso_km_company";
import DirectionsCarFilledOutlinedIcon from '@mui/icons-material/DirectionsCarFilledOutlined';
import CreditCardOutlinedIcon from '@mui/icons-material/CreditCardOutlined';
import FilePreviewModal from "./preview_modal";
import Fetching from "./fetching";

const PAGE_SIZE = 10;

function RefundRowDetails({ refund, employeeId, section, onAction, submitForm, afterSubmitEditRecord, openRowId, setOpenRowId, editRecordId, setEditRecordId }) {
  const [editingRateId, setEditingRateId] = useState(null);
  const [editedRate, setEditedRate] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  const isEditing = editRecordId === refund.id;
  const isOpen = openRowId === refund.id;
  const toggleOpen = () => setOpenRowId(isOpen ? null : refund.id);

  const departure = refund.departure_location ?? refund.from ?? refund.origin ?? '-';
  const arrival   = refund.arrival_location   ?? refund.to   ?? refund.destination ?? '-';
  const distance  = Number(refund?.distance_km ? refund.distance_km : '');

  const effectiveRate =
    refund.rate_per_km ??
    refund.vehicle?.rate_per_km ??
    null;


  return (
    <>
      <TableRow
        sx={{ cursor: 'pointer' }}
        onClick={toggleOpen}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && toggleOpen()}
        hover
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => { e.stopPropagation(); toggleOpen(); }}
          >
            {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
        </TableCell>

        {/* Data */}
        <TableCell>{moment(refund.date || refund.created_at).format('DD/MM/YYYY')}</TableCell>

        {/* Tipo */}
        <TableCell>
          <span
            className={`inline-flex items-center gap-1 text-xs font-semibold rounded-full px-2 py-1
              ${refund.type === 'mileage'
                ? 'bg-orange-100 text-orange-700'
                : refund.type === 'regular'
                ? 'bg-indigo-100 text-indigo-800'
                : 'bg-gray-100 text-gray-800'}`}
          >
            {refund.type === 'mileage' ? (
              <CustomTooltipComponent title="Rimborso Chilometrico" placement="top-start" arrow>
                <DirectionsCarFilledOutlinedIcon style={{ fontSize: '20px' }} />
              </CustomTooltipComponent>
            ) : refund.type === 'regular' ? (
              <CustomTooltipComponent title="Rimborso Spesa" placement="top-start" arrow>
                <CreditCardOutlinedIcon style={{ fontSize: '18px' }} />
              </CustomTooltipComponent>
            ) : null}
          </span>
        </TableCell>

        {/* Importo (sempre dal backend) */}
        <TableCell>
          {(refund.amount) ? (
            <span
              className={
                refund.type === 'regular'
                  ? 'text-indigo-500 font-semibold'
                  : (refund.status === 'rejected' ? 'text-gray-400' : 'text-orange-500 font-semibold')
              }
            >
              € {Number(refund.amount).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </span>
          ) : (
            <span className="text-gray-600 pl-6">/ </span>
          )}
        </TableCell>

        {/* KM (solo mileage) */}
        <TableCell>
          {refund.type === 'mileage'
            ? (Number.isFinite(distance) && distance > 0
                ? `${distance.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} km`
                : '–')
            : ''}
        </TableCell>

        {/* Tariffa per KM (inline edit a livello di RICHIESTA) */}
        <TableCell>
          {refund.type === 'mileage' ? (
            editingRateId === refund.id ? (
              <div className="flex items-center gap-2">
                <input
                  type="number"
                  step="0.01"
                  className="border border-gray-300 rounded px-2 py-1 w-[90px] text-sm"
                  value={editedRate}
                  onChange={(e) => setEditedRate(e.target.value)}
                  placeholder="€/km"
                />
                <button
                  className="text-indigo-600 text-xs font-semibold hover:underline"
                  onClick={(e) => {
                    e.stopPropagation();
                    onAction?.(
                      'save-rate',
                      {
                        refundId: refund.id,
                        rate_per_km:
                          editedRate === '' || editedRate === null || editedRate === undefined
                            ? null
                            : Number(editedRate),
                        employeeId,
                        section,
                      },
                      'mileage',
                      () => {
                        setEditingRateId(null);
                        setEditedRate('');
                      }
                    );
                  }}
                >
                  Salva
                </button>
                
                <button
                  className="text-gray-500 text-xs hover:underline"
                  onClick={(e) => { e.stopPropagation(); setEditingRateId(null); setEditedRate(''); }}
                >
                  Annulla
                </button>
              </div>
            ) : (
              <CustomTooltipComponent title="Clicca per modificare (tariffa della richiesta)" placement="top-start" arrow>
                <div
                  className="cursor-pointer hover:text-indigo-700 text-sm"
                  onClick={(e) => { e.stopPropagation(); setEditingRateId(refund.id); setEditedRate(refund.rate_per_km || ''); }}
                >
                  {effectiveRate != null
                    ? `${Number(effectiveRate).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/km`
                    : <span className="italic text-gray-400">Clicca per inserire</span>
                  }
                </div>
              </CustomTooltipComponent>
            )
          ) : (
            <span className="text-gray-500"></span>
          )}
        </TableCell>

        {/* Azioni */}
        <TableCell align="center" onClick={(e)=>e.stopPropagation()}>
          <div className="flex gap-1 items-center">
            <CustomTooltipComponent title="Approva" placement="top-start" arrow>
              <CheckOutlinedIcon
                className="w-5 h-5 cursor-pointer text-indigo-500 hover:text-green-400"
                onClick={() => onAction?.('approved', refund, refund.type === 'regular' ? 'cost' : 'mileage')}
              />
            </CustomTooltipComponent>
            <CustomTooltipComponent title="Rifiuta" placement="top-start" arrow>
              <CloseOutlinedIcon
                className="w-5 h-5 cursor-pointer text-gray-600 hover:text-red-600"
                onClick={() => onAction?.('rejected', refund, refund.type === 'regular' ? 'cost' : 'mileage')}
              />
            </CustomTooltipComponent>
            <CustomTooltipComponent title="Modifica" placement="top-start" arrow>
              <ModeEditOutlinedIcon
                className="w-5 h-5 cursor-pointer text-gray-600 hover:text-indigo-600"
                onClick={() => setEditRecordId(refund.id)}
              />
            </CustomTooltipComponent>
            <CustomTooltipComponent title="Elimina" placement="top-start" arrow>
              <TrashIcon
                className="w-5 h-5 cursor-pointer text-gray-800 hover:text-red-600"
                onClick={() => onAction?.('delete', refund, refund.type === 'regular' ? 'cost' : 'mileage')}
              />
            </CustomTooltipComponent>
          </div>
        </TableCell>
      </TableRow>

      {/* DETTAGLIO */}
      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={10}>
          <Collapse in={isOpen && !isEditing} timeout="auto" unmountOnExit>
            <Box margin={1}>
              <div className="text-md uppercase font-semibold text-indigo-800 pb-4 pl-14">
                Dettagli richiesta
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pl-14">
                {/* Colonna sx */}
                <div className="space-y-2">
                  <div><strong>Oggetto:</strong> {refund.title}</div>
                  <div>
                    <strong>Data:</strong>{" "}
                    {(() => {
                      const d = refund?.date ?? refund?.created_at;
                      return d ? moment(d).format('DD/MM/YYYY') : '-';
                    })()}
                  </div>

                  {refund.type === 'mileage' && (
                    <>
                      <div><strong>Partenza:</strong> {departure}</div>
                      <div><strong>Arrivo:</strong> {arrival}</div>
                      <div>
                        <strong>Distanza:</strong>{' '}
                        {Number.isFinite(distance)
                          ? `${distance.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} km`
                          : ' '}
                      </div>
                      <div>
                        <strong>Tariffa richiesta:</strong>{' '}
                        {refund.rate_per_km != null
                          ? `${Number(refund.rate_per_km).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/km`
                          : ' '}
                      </div>
                      <div>
                        <strong>Tariffa veicolo:</strong>{' '}
                        {refund.vehicle?.rate_per_km != null
                          ? `${Number(refund.vehicle.rate_per_km).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €/km`
                          : ' '}
                      </div>
                      <div>
                        <strong>Tariffa usata:</strong>{' '}
                          {refund.rate_per_km != null
                            ? 'personalizzata richiesta'
                            : (refund.vehicle?.rate_per_km != null ? 'default veicolo' : '')}

                      </div>
                    </>
                  )}

                  {/* IMPORTO nel dettaglio */}
                  {refund.type === 'mileage' ? (
                    <div>
                      <strong>Importo:</strong>{' '}
                      {Number.isFinite(Number(refund.amount)) && Number(refund.amount) > 0 ? (
                        <>€ {Number(refund.amount).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</>
                      ) : (
                        <span className="text-gray-500">
                          salvare la tariffa per km per ottenere l'importo del rimborso
                        </span>
                      )}
                    </div>
                  ) : (
                    Number(refund.amount) > 0 && (
                      <div>
                        <strong>Importo:</strong>{' '}
                        € {Number(refund.amount).toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                      </div>
                    )
                  )}

                  {refund.description && (
                    <div className="pt-2">
                      <strong>Descrizione:</strong>
                      <div
                        className="mt-1 text-sm text-gray-800 prose max-w-none"
                        dangerouslySetInnerHTML={{ __html: refund.description }}
                      />
                    </div>
                  )}

                  {refund.file && (
                    <div>
                      <strong>Allegati:</strong>{" "}
                      <button
                        type="button"
                        onClick={() => setPreviewDoc({ url: refund.file, name: refund.title })}
                        className="text-sm font-semibold bg-indigo-500 hover:bg-indigo-400 p-1 ml-2 text-white rounded-md"
                      >
                        Visualizza
                      </button>
                    </div>
                  )}
                </div>

                {/* Colonna dx */}
                <div className="space-y-2">
                  {refund.type === 'mileage' && refund.vehicle && (
                    <>
                      <div><strong>Marca veicolo:</strong> {refund.vehicle.vehicle_brand || '-'}</div>
                      <div><strong>Modello:</strong> {refund.vehicle.vehicle_type || '-'}</div>
                      <div><strong>Alimentazione:</strong> {refund.vehicle.vehicle_motor || '-'}</div>
                      <div><strong>Targa:</strong> {refund.vehicle.license_plate || '-'}</div>
                    </>
                  )}

                  {refund.status !== null && refund.reviewed_on && (
                    <div className="pt-4">
                      <strong>Elaborata il:</strong>{' '}
                      <time dateTime={refund.reviewed_on}>
                        {moment(refund.reviewed_on).format('DD/MM/YYYY, HH:mm')}
                      </time>
                    </div>
                  )}
                </div>
              </div>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>

      {/* FORM MODIFICA */}
      {editRecordId === refund.id && (
        <tr>
          <td colSpan="10">
            {refund.type === 'mileage' ? (
              <FormEditRimborsoKmCompany
                submitForm={submitForm}
                afterSubmitEditRecord={afterSubmitEditRecord}
                recordToEdit={refund}
                onCancelEdit={() => setEditRecordId(null)}
              />
            ) : (
              <FormEditRimborsoCompany
                submitForm={submitForm}
                afterSubmitEditRecord={afterSubmitEditRecord}
                recordToEdit={refund}
                onCancelEdit={() => setEditRecordId(null)}
              />
            )}
          </td>
        </tr>
      )}

      <FilePreviewModal doc={previewDoc} onClose={() => setPreviewDoc(null)} />
    </>
  );
}


function Row({
  row,
  refundsCache,
  onOpenEmployee,
  onProcessAllApproved,
  onProcessAllRejected,
  onRefundAction,
  submitForm,
  afterSubmitEditRecord,
  editRecordId,
  setEditRecordId,
  openEmployeeCf,
  setOpenEmployeeCf,
  openRowId,
  setOpenRowId
}) {
  const { employee } = row;
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // pagination locale (per employee)
  const [detailsPage, setDetailsPage] = useState(1);

  const isOpen = openEmployeeCf === employee.cf;

  // refunds completi (cache o fallback) + sort stabile
  const refundsAll = useMemo(() => {
    const list = refundsCache?.[employee.id] || row.refunds || [];
    return [...list].sort((a, b) => {
      const da = new Date(a?.date || a?.created_at || 0).getTime();
      const db = new Date(b?.date || b?.created_at || 0).getTime();
      return db - da;
    });
  }, [refundsCache, employee.id, row.refunds]);

  const pageCount = useMemo(
    () => Math.max(1, Math.ceil(refundsAll.length / PAGE_SIZE)),
    [refundsAll.length]
  );

  const refundsPage = useMemo(() => {
    const start = (detailsPage - 1) * PAGE_SIZE;
    return refundsAll.slice(start, start + PAGE_SIZE);
  }, [refundsAll, detailsPage]);

  // reset pagina / clamp dopo update/delete
  useEffect(() => {
    if (!isOpen) {
      setDetailsPage(1);
      return;
    }
    setDetailsPage((p) => Math.min(p, pageCount));
  }, [isOpen, pageCount]);

  const handleToggleOpen = async () => {
    const willOpen = !isOpen;
    setOpenEmployeeCf(willOpen ? employee.cf : null);

    if (willOpen) {
      setDetailsPage(1);
      setOpenRowId(null);

      if (!refundsCache?.[employee.id] && typeof onOpenEmployee === 'function') {
        setIsLoadingDetails(true);
        try {
          await onOpenEmployee(employee);
        } finally {
          setIsLoadingDetails(false);
        }
      }
    }
  };

  // --- aggregati (summary) ---
  const aggRequests   = typeof row.requests === 'number' ? row.requests : null;
  const aggTotalAmt   = row.total_amount != null ? Number(row.total_amount) : null;
  const aggTotalKm    = row.total_km     != null ? Number(row.total_km)     : null;
  const aggTotalMileage = row.total_mileage_amount != null ? Number(row.total_mileage_amount) : null;
  const aggTotalRegular = row.total_regular_amount != null ? Number(row.total_regular_amount) : null;

  const totalRegularAmountLocal = refundsAll.length
    ? refundsAll.reduce((sum, r) => (r.type === 'regular' ? sum + Number(r.amount || 0) : sum), 0)
    : null;

  const totalMileageAmountLocal = refundsAll.length
    ? refundsAll.reduce((sum, r) => {
        if (r.type !== 'mileage') return sum;
        const distance = Number(r.distance_km ?? r.km ?? r.distance ?? 0);
        const rate     = r.rate_per_km ?? r.vehicle?.rate_per_km;
        const amount   = Number.isFinite(Number(r.amount))
          ? Number(r.amount)
          : (Number.isFinite(distance) && Number.isFinite(rate) ? distance * Number(rate) : 0);
        return sum + amount;
      }, 0)
    : null;

  const totalAmount = aggTotalAmt != null
    ? aggTotalAmt
    : refundsAll.length
      ? refundsAll.reduce((sum, r) => {
          if (r.type === 'regular') return sum + Number(r.amount || 0);
          const distance = Number(r.distance_km ?? r.km ?? r.distance ?? 0);
          const rate = r.rate_per_km ?? r.vehicle?.rate_per_km;
          const amount = Number.isFinite(Number(r.amount))
            ? Number(r.amount)
            : (Number.isFinite(distance) && Number.isFinite(rate) ? distance * Number(rate) : 0);
          return sum + amount;
        }, 0)
      : 0;

  const totalMileage = aggTotalKm != null
    ? aggTotalKm
    : refundsAll.length
      ? refundsAll.reduce(
          (sum, r) => r.type === 'mileage'
            ? sum + Number(r.distance_km ?? r.km ?? r.distance ?? 0)
            : sum,
          0
        )
      : 0;

  const totalPendingRequests = aggRequests != null ? aggRequests : refundsAll.length;

  const handleAfterSubmitEditRecord = async (updatedRecord) => {
    await afterSubmitEditRecord(updatedRecord);
    setEditRecordId(null);
    if (updatedRecord?.employee?.cf) setOpenEmployeeCf(updatedRecord.employee.cf);
  };

  return (
    <React.Fragment>
      <TableRow
        sx={{ '& > *': { borderBottom: 'unset' }, cursor: 'pointer' }}
        onClick={handleToggleOpen}
        role="button"
        hover
        tabIndex={0}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && handleToggleOpen()}
      >
        <TableCell>
          <IconButton
            aria-label="expand row"
            size="small"
            onClick={(e) => { e.stopPropagation(); handleToggleOpen(); }}
          >
            {isOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>

        <TableCell>
          {employee.last_name && employee.first_name
            ? `${employee.last_name} ${employee.first_name}`
            : employee.cf}
        </TableCell>

        <TableCell align="center">{totalPendingRequests}</TableCell>

        <CustomTooltipComponent
          title={
            <div>
              <div>
                Totale Rimborsi spese:{' '}
                <strong>
                  € {Number(aggTotalRegular ?? totalRegularAmountLocal ?? 0)
                        .toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </strong>
              </div>
              <div>
                Totale Rimborsi KM:{' '}
                <strong>
                  € {Number(aggTotalMileage ?? totalMileageAmountLocal ?? 0)
                        .toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                </strong>
              </div>
            </div>
          }
          placement="top-start"
          arrow
          PopperProps={{
            modifiers: [
                {
                name: 'offset',
                options: {
                    offset: [0, -30], 
                },
                },
            ],
          }}
        >
          <TableCell align="center">
            {totalAmount > 0 &&
              `€ ${totalAmount.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          </TableCell>
        </CustomTooltipComponent>

        <TableCell align="center">
          {totalMileage > 0 &&
            `${totalMileage.toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} KM`}
        </TableCell>

        <TableCell align="center">
          <div className="flex gap-1 items-center">
            <CustomTooltipComponent title={`Approva tutte (${totalPendingRequests})`} placement="top-start" arrow>
              <CheckOutlinedIcon
                className="w-5 h-5 cursor-pointer text-indigo-500 hover:text-green-400"
                onClick={(e) => { e.stopPropagation(); onProcessAllApproved(employee, refundsAll, totalPendingRequests); }}
              />
            </CustomTooltipComponent>
            <CustomTooltipComponent title="Rifiuta tutte" placement="top-start" arrow>
              <CloseOutlinedIcon
                className="w-5 h-5 cursor-pointer text-gray-600 hover:text-red-600"
                onClick={(e) => { e.stopPropagation(); onProcessAllRejected(employee, refundsAll, totalPendingRequests); }}
              />
            </CustomTooltipComponent>
          </div>
        </TableCell>
      </TableRow>

      <TableRow>
        <TableCell style={{ paddingBottom: 0, paddingTop: 0 }} colSpan={6} className='bg-indigo-50'>
          <Collapse in={isOpen} timeout="auto" unmountOnExit>
            <Box sx={{ margin: 2 }}>
              <Typography variant="p" gutterBottom component="div" className='font-semibold text-indigo-800 uppercase'>
                Richieste da gestire di {employee.last_name && employee.first_name
                  ? `${employee.last_name} ${employee.first_name}`
                  : employee.cf}
              </Typography>

              <Table size="small" aria-label="rimborsi" className="rounded-lg">
                <TableHead>
                  <TableRow>
                    <TableCell style={{ width: '5%' }}></TableCell>
                    <TableCell style={{ width: '10%' }}>Data</TableCell>
                    <TableCell style={{ width: '15%' }}>Tipo</TableCell>
                    <TableCell style={{ width: '15%' }}>Importo</TableCell>
                    <TableCell style={{ width: '15%' }}>KM</TableCell>
                    <TableCell style={{ width: '20%', paddingLeft: 0 }}>Tariffa per KM</TableCell>
                    <TableCell style={{ width: '20%' }}></TableCell>
                  </TableRow>
                </TableHead>

                <TableBody className="bg-gray-50 shadow-lg">
                  {isLoadingDetails ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" className="py-6">
                        <Fetching />
                      </TableCell>
                    </TableRow>
                  ) : refundsAll.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" className="py-6 text-sm text-gray-500 italic">
                        Dettagli non disponibili.
                      </TableCell>
                    </TableRow>
                  ) : (
                    refundsPage.map((refund) => (
                      <RefundRowDetails
                        key={refund.id}
                        refund={refund}
                        employeeId={employee.id}
                        section="pending"
                        openRowId={openRowId}
                        setOpenRowId={setOpenRowId}
                        onAction={(actionType, rr, refundKind, onSuccessCallback) =>
                          onRefundAction(actionType, rr, refundKind, onSuccessCallback)
                        }
                        submitForm={submitForm}
                        afterSubmitEditRecord={handleAfterSubmitEditRecord}
                        editRecordId={editRecordId}
                        setEditRecordId={setEditRecordId}
                      />
                    ))
                  )}

                  {/* PAGINATION (solo se serve) */}
                  {!isLoadingDetails && refundsAll.length > PAGE_SIZE && (
                    <TableRow>
                      <TableCell colSpan={7} align="center" className="py-4">
                        <div className="flex flex-col items-center gap-2">
                          <Pagination
                            count={pageCount}
                            page={detailsPage}
                            onChange={(_, value) => {
                              setDetailsPage(value);
                              setOpenRowId(null);
                            }}
                            shape="rounded"
                          />
                          <div className="text-xs text-gray-600">
                            Pagina {detailsPage} di {pageCount} · {refundsAll.length} richieste totali
                          </div>
                        </div>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </React.Fragment>
  );
}

PendingRefundRequests.propTypes = {
  groupedRecords: PropTypes.arrayOf(
    PropTypes.shape({
      employee: PropTypes.shape({
        id:        PropTypes.number,
        cf:        PropTypes.string,
        first_name:PropTypes.string,
        last_name: PropTypes.string,
      }).isRequired,
      // opzionale: la summary potrebbe non includerlo
      refunds: PropTypes.arrayOf(PropTypes.object),
      // campi aggregati dalla summary (opzionali)
      requests:     PropTypes.number,
      total_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      total_km:     PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      total_mileage_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      total_regular_amount: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    })
  ).isRequired,

  // nuove prop se usi lazy-load
  refundsCache:   PropTypes.object, // { [employeeId]: Refund[] }
  onOpenEmployee: PropTypes.func,

  onProcessAllApproved: PropTypes.func,
  onProcessAllRejected: PropTypes.func,
  totalPendingCount: PropTypes.number,
  globalPendingCount: PropTypes.number,
  handleProcessAllRequests: PropTypes.func,
  onRefundAction: PropTypes.func.isRequired,
  submitForm: PropTypes.func.isRequired,
  afterSubmitEditRecord: PropTypes.func.isRequired,
  setOpenEmployeeCf: PropTypes.func.isRequired,
  openEmployeeCf: PropTypes.any,
};

PendingRefundRequests.defaultProps = {
  groupedRecords: [],
  refundsCache: {},
  onOpenEmployee: () => {},
};

export default function PendingRefundRequests({ groupedRecords, refundsCache, onOpenEmployee, onProcessAllApproved, onProcessAllRejected, totalPendingCount, globalPendingCount, handleProcessAllRequests, onRefundAction, submitForm, afterSubmitEditRecord, setOpenEmployeeCf, openEmployeeCf }) {
  const [editRecordId, setEditRecordId] = useState(null);
  const [openRowId, setOpenRowId] = useState(null);
  
  return (
    <TableContainer component={Paper} className='border border-gray-300'>
      <div className='flex justify-between items-center my-4 px-4'>
        <div className='text-left font-semibold text-indigo-800 uppercase'>
          <HourglassTopIcon className='inline-block w-5 h-5 mr-2' />
          Richieste da approvare
        </div>
        {(globalPendingCount ?? totalPendingCount) > 0 && (
          <div className="flex gap-2">
            <button
              onClick={() => handleProcessAllRequests('approved')}
              className="text-sm px-4 py-1 rounded-md font-semibold whitespace-nowrap bg-indigo-600 text-white hover:bg-indigo-500"
            >
              Approva tutte ({globalPendingCount ?? totalPendingCount})
            </button>
            <button
              onClick={() => handleProcessAllRequests('rejected')}
              className="text-sm font-semibold text-gray-600 bg-gray-200 px-4 py-1 rounded hover:bg-gray-300"
            >
              Rifiuta tutte ({globalPendingCount ?? totalPendingCount})
            </button>
          </div>
        )}
      </div>

      <Table aria-label="collapsible table">
        <TableHead className="border border-gray-300 rounded-md">
          <TableRow className="border border-gray-300 rounded-md">
            <TableCell />
            <TableCell>Dipendente</TableCell>
            <TableCell align="center">N. Richieste</TableCell>
            <TableCell align="center">Totale Importo Rimborso</TableCell>
            <TableCell align="center">Totale KM</TableCell>
            <TableCell align="center"></TableCell>
          </TableRow>
        </TableHead>
        <TableBody className="border border-gray-300 rounded-md">
          {groupedRecords.length === 0 ? (
            <TableRow>
              <TableCell colSpan={6} align="center" className="py-10 italic text-sm">
                <div className="flex flex-col items-center space-y-2">
                 
                  <span className="text-gray-500">Nessuna richiesta da elaborare presente per questo periodo</span>
                </div>
              </TableCell>
            </TableRow>
          ) : (
            groupedRecords.map((group) => (
              <Row
                key={group.employee.cf}
                row={group}
                refundsCache={refundsCache}
                onOpenEmployee={onOpenEmployee}
                onProcessAllApproved={onProcessAllApproved}
                onProcessAllRejected={onProcessAllRejected}
                onRefundAction={onRefundAction}
                submitForm={submitForm}
                afterSubmitEditRecord={afterSubmitEditRecord}
                editRecordId={editRecordId}
                setEditRecordId={setEditRecordId}
                openEmployeeCf={openEmployeeCf}
                setOpenEmployeeCf={setOpenEmployeeCf}
                openRowId={openRowId}
                setOpenRowId={setOpenRowId}
              />
            ))
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
